import axios from "axios";
import axiosInstance from "../scenes/utils/axiosInstance";
import authHttpClient from "../scenes/utils/authHttpClient";
import { API_BASE_URL, API_ENDPOINTS } from "../config/api";
import { withExportLang } from "../utils/getExportLangParam";
import { getDoctorSessionCacheKey } from "../utils/authSession";

const doctorCache = new Map();
const inFlightDoctorRequests = new Map();
const CACHE_TTL_MS = 3 * 60 * 1000;

export function clearDoctorCache() {
  doctorCache.clear();
  inFlightDoctorRequests.clear();
}

export default class DoctorService {
  toggleDoctorStatus = async (id, active) => {
    const response = await authHttpClient.put(`/api/doctors/${id}/status`, { active });
    return response.data;
  }

  doctorLogin(doctorData) {
    return authHttpClient.post(API_ENDPOINTS.AUTH.LOGIN_DOCTOR, doctorData);
  }
  
  getDoctorInfo = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const doctorId = localStorage.getItem("doctorId") || user.id || "me";
      const cacheKey = `info_${doctorId}`;

      if (doctorCache.has(cacheKey)) {
        return doctorCache.get(cacheKey);
      }
      
      if (inFlightDoctorRequests.has(cacheKey)) {
        return inFlightDoctorRequests.get(cacheKey);
      }

      const requestPromise = axiosInstance.get(API_ENDPOINTS.DOCTORS.BY_ID(doctorId)).then(response => {
        doctorCache.set(cacheKey, response.data);
        setTimeout(() => doctorCache.delete(cacheKey), CACHE_TTL_MS);
        inFlightDoctorRequests.delete(cacheKey);
        return response.data;
      }).catch(error => {
        inFlightDoctorRequests.delete(cacheKey);
        throw error;
      });

      inFlightDoctorRequests.set(cacheKey, requestPromise);
      return requestPromise;
    } catch (error) {
      console.error("Error fetching doctor info:", error?.message);
      throw error;
    }
  };

  // Get another doctor's info by ID (for viewing subordinate doctors)
  getDoctorInfoById = async (doctorId) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.DOCTORS.BY_ID(doctorId)
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching doctor info by ID:", error);
      throw error;
    }
  };

  registerDoctor = async (data) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.AUTH.ADMIN_REGISTER_DOCTOR, data);
      clearDoctorCache();
      return response.data;
    } catch (error) {
      console.error("Ekleme Sırasında Hata Meydana Geldi:", error);
      throw error;
    }
  };

  registerIndividualDoctor = async (data, recaptchaToken = null) => {
    try {
      const headers = {};
      if (recaptchaToken) headers["x-recaptcha-token"] = recaptchaToken;
      const response = await axiosInstance.post(API_ENDPOINTS.AUTH.INDIVIDUAL_DOCTOR_REGISTER, data, { headers });
      return response.data;
    } catch (error) {
      console.error("Bireysel Hekim Kayıt Hatası:", error);
      throw error;
    }
  };

  getAllDoctor = async (options = {}) => {
    try {
      const _uid = getDoctorSessionCacheKey();
      const cacheKey = JSON.stringify({ _uid, ...options });
      const now = Date.now();
      const cached = doctorCache.get(cacheKey);
      if (cached && now - cached.timestamp < CACHE_TTL_MS) {
        return cached.data;
      }
      if (inFlightDoctorRequests.has(cacheKey)) {
        return await inFlightDoctorRequests.get(cacheKey);
      }

      const fetchPromise = (async () => {
        try {
          const response = await axiosInstance.get(API_ENDPOINTS.DOCTORS.ALL, options);
          let result;
          if (options.withMeta) {
            result = { data: response.data, headers: response.headers };
          } else {
            result = response.data;
          }
          doctorCache.set(cacheKey, { data: result, timestamp: Date.now() });
          return result;
        } finally {
          inFlightDoctorRequests.delete(cacheKey);
        }
      })();

      inFlightDoctorRequests.set(cacheKey, fetchPromise);
      return await fetchPromise;
    } catch (error) {
      console.log("Bir sorun oluştu.");
    }
  };

  getSuggestions = async (q, limit = 20) => {
    const response = await axiosInstance.get(API_ENDPOINTS.DOCTORS.SUGGESTIONS, { params: { q, limit } });
    return response.data;
  }

  exportDoctorsExcel = async (filters = {}) => {
    const response = await axiosInstance.get(API_ENDPOINTS.DOCTORS.EXPORT_EXCEL, {
      params: withExportLang(filters),
      responseType: "blob",
    });
    return response;
  }
  
  addDoctor = async (data)=>{
    try{
      const response = await axiosInstance.post(API_ENDPOINTS.AUTH.ADMIN_REGISTER_DOCTOR, data)
      return response.data
    }catch(error){
      console.log("Bir sorun oluştu.")
    }
  }
  
  // 🆕 YENİ: Alt doktor davet etme (email ile + permission groups)
  inviteDoctor = async (email, role_title, permissionGroupIds = null) => {
    try {
      const requestBody = { email, role_title };
      
      // Permission group IDs varsa ekle
      if (permissionGroupIds && Array.isArray(permissionGroupIds) && permissionGroupIds.length > 0) {
        requestBody.permissionGroupIds = permissionGroupIds;
      }
      
      const response = await axiosInstance.post(
        API_ENDPOINTS.AUTH.ADMIN_INVITE_DOCTOR,
        requestBody
      );
      return response.data;
    } catch (error) {
      console.error("Doktor daveti gönderilirken hata oluştu:", error);
      throw error;
    }
  }
  requestPasswordOtp = async (email) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.PASSWORD_FORGOT}`,
        { email, userType: "doctor" }
      );
      return response.data;
    } catch (error) {
      console.error("Şifre sıfırlama OTP isteğinde hata oluştu:", error);
      throw error;
    }
  };

  verifyPasswordOtp = async ({ email, otp }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.PASSWORD_VERIFY_OTP}`,
        { email, otp }
      );
      return response.data;
    } catch (error) {
      console.error("OTP doğrulanırken hata oluştu:", error);
      throw error;
    }
  };

  resetPassword = async ({ resetToken, newPassword, confirmPassword }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.PASSWORD_RESET}`,
        {
          resetToken,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Şifre sıfırlanırken hata oluştu:", error);
      throw error;
    }
  };

  // 2FA (opsiyonel e-posta tabanlı 2 Adımlı Doğrulama) — Profil > Güvenlik
  getTwoFactorStatus = async () => {
    const response = await axiosInstance.get(API_ENDPOINTS.AUTH.TWO_FACTOR_STATUS);
    return response.data;
  };

  requestTwoFactorActivation = async () => {
    const response = await axiosInstance.post(API_ENDPOINTS.AUTH.TWO_FACTOR_ENABLE_REQUEST);
    return response.data;
  };

  verifyTwoFactorActivation = async (otp) => {
    const response = await axiosInstance.post(API_ENDPOINTS.AUTH.TWO_FACTOR_ENABLE_VERIFY, { otp });
    return response.data;
  };

  disableTwoFactor = async (password) => {
    const response = await axiosInstance.post(API_ENDPOINTS.AUTH.TWO_FACTOR_DISABLE, { password });
    return response.data;
  };

  // Login akışı sırasında (henüz authMiddleware'den geçmeden) OTP doğrulama.
  verifyTwoFactorLogin = async ({ pendingToken, otp }) => {
    const response = await authHttpClient.post(API_ENDPOINTS.AUTH.TWO_FACTOR_VERIFY_LOGIN, {
      pendingToken,
      otp,
    });
    return response.data;
  };

  // Update doctor information
  updateDoctor = async (doctorId, updateData) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.DOCTORS.UPDATE(doctorId),
        updateData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating doctor:", error);
      throw error;
    }
  };
}
